import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/EditCollectionAndDelivery.css';
import { FaCloudUploadAlt, FaSpinner, FaTimes, FaTrashAlt } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { auth, db, storage } from '../firebaseConfig';
import {
  doc, getDoc, updateDoc,
  collection, getDocs, where, query
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/* ───────── constants & helpers ───────── */
const fileKind = (name, type) => {
  if (/pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/csv$/i.test(name) || type === 'text/csv') return 'csv';
  if (/image\//.test(type)) return 'image';
  return 'other';
};
const MAX_FILES = 10, MAX_SIZE_MB = 10;
const TRIP_TYPES = ['Delivery', 'Collection'];
const TRIP_STATUSES = ['Scheduled', 'Done'];
const OVERNIGHT_EXTRA = 650;

export default function EditCollectionAndDelivery() {
  /* URL param */
  const { id } = useParams(); // Firestore doc.id for this Trip
  const navigate = useNavigate();

  /* base trip fields */
  const [tripId, setTripId] = useState('');
  const [tripType, setTripType] = useState(TRIP_TYPES[0]);
  const [tripDate, setTripDate] = useState('');
  const [tripStatus, setTripStatus] = useState(TRIP_STATUSES[0]);

  /* dropdown sources */
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [tripCosts, setTripCosts] = useState([]);

  /* selections */
  const [driverSelId, setDriverSelId] = useState('');
  const [vehicleSelId, setVehicleSelId] = useState('');
  const [assignedId, setAssignedId] = useState('');
  const [tripCostTemplateId, setTripCostTemplateId] = useState('');

  /* costing */
  const [baseCost, setBaseCost] = useState('');
  const [sleepover, setSleepover] = useState(false);

  const totalCost = useMemo(() => {
    const base = parseFloat(baseCost) || 0;
    return base + (sleepover ? OVERNIGHT_EXTRA : 0);
  }, [baseCost, sleepover]);

  /* attachments */
  const [oldFiles, setOldFiles] = useState([]);          // {name,url,kind}[]
  const [removeOld, setRemoveOld] = useState(new Set()); // urls marked for deletion
  const [files, setFiles] = useState([]);                // File[]
  const [fileMeta, setFileMeta] = useState([]);          // {name,kind,preview?}
  const [dragActive, setDrag] = useState(false);

  /* ui */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* validation */
  const [errors, setErrors] = useState({
    driverSelId:false, vehicleSelId:false, assignedId:false
  });

  const fileInputRef = useRef(null);

  /* ───────── initial fetch ───────── */
  useEffect(() => {
    const init = async user => {
      if (!user) { toast.error('Please login to edit.'); setLoading(false); return; }
      try {
        setLoading(true);

        // fetch trip
        const snap = await getDoc(doc(db, 'Trips', id));
        if (!snap.exists()) { toast.error('Trip not found.'); navigate('/Trips'); return; }
        const d = snap.data();

        // base info
        setTripId(d.tripId || '');
        setTripType(d.tripType || TRIP_TYPES[0]);
        setTripDate(d.tripDate || '');
        setTripStatus(d.tripStatus || d.trip_status || TRIP_STATUSES[0]);

        // costing (back-compat with older docs)
        setTripCostTemplateId(d.tripCostTemplateId || '');
        setBaseCost(
          d.tripCostBase != null ? String(d.tripCostBase)
          : d.tripCost != null ? String(d.tripCost) // older field; let user edit
          : ''
        );
        setSleepover(Boolean(d.sleepover) || (d.sleepoverExtra > 0));

        // existing attachments
        setOldFiles(d.fileMeta || []);

        // dropdown sources
        const [drvSnap, vehSnap, jobSnap, rfqSnap, costSnap] = await Promise.all([
          getDocs(collection(db, 'Drivers')),
          getDocs(collection(db, 'Vehicles')),
          getDocs(collection(db, 'Jobs')),
          getDocs(collection(db, 'RFQs')),
          getDocs(collection(db, 'TripCosts')),
        ]);

        const drvArr = drvSnap.docs.map(x => ({ id: x.id, ...x.data() }));
        const vehArr = vehSnap.docs.map(x => ({ id: x.id, ...x.data() }));
        const jobArr = jobSnap.docs.map(x => ({ id: x.id, ...x.data() }));
        const rfqArr = rfqSnap.docs.map(x => ({ id: x.id, ...x.data() }));
        const costArr = costSnap.docs.map(x => ({ id: x.id, ...x.data() }));

        setDrivers(drvArr);
        setVehicles(vehArr);
        setJobs(jobArr);
        setRfqs(rfqArr);
        setTripCosts(costArr);

        // preselect dropdowns from saved doc (back-compat)
        setDriverSelId(d.driverRefId || '');
        setVehicleSelId(d.vehicleRefId || '');
        setAssignedId(d.assignedId || '');

        // if no refs saved, best-effort match by name/plate
        if (!d.driverRefId && d.driver?.name) {
          const match = drvArr.find(x => (x.name || '').trim().toLowerCase() === d.driver.name.trim().toLowerCase());
          if (match) setDriverSelId(match.id);
        }
        if (!d.vehicleRefId && (d.vehicle?.name || d.vehicle?.vehicleName)) {
          const name = (d.vehicle.name || d.vehicle.vehicleName || '').trim().toLowerCase();
          const match = vehArr.find(x => (x.name || '').trim().toLowerCase() === name);
          if (match) setVehicleSelId(match.id);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load data.');
      } finally { setLoading(false); }
    };

    const unsub = auth.onAuthStateChanged(init);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ───────── validation ───────── */
  const validate = () => {
    const v = {
      driverSelId: !driverSelId,
      vehicleSelId: !vehicleSelId,
      assignedId: !assignedId
    };
    setErrors(v);
    return !Object.values(v).some(Boolean);
  };

  /* ───────── file helpers ───────── */
  const addFiles = newFiles => {
    if (!newFiles.length) return;
    const exist = [...files], meta = [...fileMeta];
    for (const f of newFiles) {
      if (exist.length + oldFiles.length - removeOld.size >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files reached.`); break;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB.`); continue;
      }
      const kind = fileKind(f.name, f.type);
      exist.push(f);
      meta.push({ name: f.name, kind, preview: kind === 'image' ? URL.createObjectURL(f) : null });
    }
    setFiles(exist); setFileMeta(meta);
  };
  const handleInputFiles = e => addFiles(Array.from(e.target.files || []));
  const onDragOver = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop = e => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files || [])); };
  const openPicker = () => fileInputRef.current?.click();

  const toggleRemoveOld = url => {
    setRemoveOld(s => {
      const n = new Set(s);
      n.has(url) ? n.delete(url) : n.add(url);
      return n;
    });
  };
  const removeNew = idx => {
    setFiles(f => f.filter((_, i) => i !== idx));
    setFileMeta(m => {
      const t = m[idx]; if (t?.preview) URL.revokeObjectURL(t.preview);
      return m.filter((_, i) => i !== idx);
    });
  };
  useEffect(() => () => fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview)), [fileMeta]);

  const uploadNew = async () => {
    if (!files.length) return { newMeta: [], newUrls: [] };
    const uploaded = await Promise.all(
      files.map(async f => {
        const path = `Trips/${tripId}/${Date.now()}_${f.name}`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, f);
        const url = await getDownloadURL(sRef);
        return { name: f.name, url, kind: fileKind(f.name, f.type) };
      })
    );
    return { newMeta: uploaded, newUrls: uploaded.map(u => u.url) };
  };

  const deleteMarked = async () => {
    if (!removeOld.size) return;
    for (const url of removeOld) {
      try { await deleteObject(ref(storage, url)); } catch { /* ignore */ }
    }
  };

  /* ───────── interactions ───────── */
  const onPickTemplate = e => {
    const id = e.target.value;
    setTripCostTemplateId(id);
    const sel = tripCosts.find(c => c.id === id);
    setBaseCost(sel ? Number(sel.amount || 0) : '');
  };

  const onToggleSleepover = checked => {
    setSleepover(checked);
    if (checked) toast.info(`R${OVERNIGHT_EXTRA} overnight extra applied to total.`);
  };

  /* ───────── save ───────── */
  const save = async e => {
    e.preventDefault();
    if (!validate()) { toast.error('Fix highlighted fields.'); return; }
    if (!auth.currentUser) { toast.error('Please login first.'); return; }

    try {
      setSaving(true);
      const { newMeta } = await uploadNew();
      await deleteMarked();

      const updatedMeta = [
        ...oldFiles.filter(f => !removeOld.has(f.url)),
        ...newMeta
      ];

      // find selected driver/vehicle full objects
      const driverSel = drivers.find(d => d.id === driverSelId) || null;
      const vehicleSel = vehicles.find(v => v.id === vehicleSelId) || null;

      await updateDoc(doc(db, 'Trips', id), {
        tripType, tripDate, tripStatus,

        // costs
        tripCostTemplateId: tripCostTemplateId || null,
        tripCostBase: parseFloat(baseCost) || 0,
        sleepover,
        sleepoverExtra: sleepover ? OVERNIGHT_EXTRA : 0,
        tripCost: totalCost,

        // driver / vehicle (refs + useful snapshot)
        driverRefId: driverSel ? driverSel.id : null,
        driver: driverSel ? {
          name: driverSel.name || '',
          licenseNumber: driverSel.licenseNumber || '',
          phone: driverSel.phone || '',
          email: driverSel.email || ''
        } : null,

        vehicleRefId: vehicleSel ? vehicleSel.id : null,
        vehicle: vehicleSel ? {
          name: vehicleSel.name || '',
          numberPlate: vehicleSel.numberPlate || '',
          make: vehicleSel.make || '',
          model: vehicleSel.model || '',
          year: vehicleSel.year || ''
        } : null,

        // assignment
        assignedId,

        // files
        fileMeta: updatedMeta,
        fileUrls: updatedMeta.map(m => m.url),

        updatedAt: new Date().toISOString()
      });

      toast.success('Trip updated!');
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error('Update failed.');
    } finally { setSaving(false); }
  };

  /* ───────── derived ───────── */
  const fileCount = useMemo(
    () => oldFiles.length - removeOld.size + files.length,
    [oldFiles, removeOld, files]
  );

  /* ───────── render ───────── */
  return (
    <div className="collection-page">
      <ToastContainer position="top-right" autoClose={2500}/>
      <Sidebar/>

      <main className="collection-main">
        <div className="title-row">
          <h2>Edit Trip</h2>
          <span className="badge-id">{tripId}</span>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FaSpinner className="spin" size={54}/>
            <p>Loading trip data…</p>
          </div>
        ) : (
          <form className="collection-form" onSubmit={save}>
            {/* Trip */}
            <section className="card">
              <h3>Trip Details</h3>
              <div className="grid-4">
                <div className="field">
                  <label>Trip ID</label>
                  <input value={tripId} readOnly/>
                </div>
                <div className="field">
                  <label>Type</label>
                  <select value={tripType} onChange={e=>setTripType(e.target.value)}>
                    {TRIP_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Date</label>
                  <input type="date" value={tripDate} onChange={e=>setTripDate(e.target.value)}/>
                </div>
                <div className="field">
                  <label>Status</label>
                  <select value={tripStatus} onChange={e=>setTripStatus(e.target.value)}>
                    {TRIP_STATUSES.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Driver & vehicle (dropdowns only) */}
            <section className="card">
              <h3>Driver & Vehicle</h3>
              <div className="grid-4">
                <div className="field">
                  <label>Driver <span className="required">*</span></label>
                  <select
                    value={driverSelId}
                    onChange={e=>setDriverSelId(e.target.value)}
                    className={errors.driverSelId ? 'input-error' : ''}
                  >
                    <option value="">— Select Driver —</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name} {d.licenseNumber ? `— ${d.licenseNumber}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.driverSelId && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>Vehicle <span className="required">*</span></label>
                  <select
                    value={vehicleSelId}
                    onChange={e=>setVehicleSelId(e.target.value)}
                    className={errors.vehicleSelId ? 'input-error' : ''}
                  >
                    <option value="">— Select Vehicle —</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} {v.numberPlate ? `— ${v.numberPlate}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleSelId && <span className="error-text">Required</span>}
                </div>
              </div>
            </section>

            {/* Assignment & Costs */}
            <section className="card">
              <h3>Assignment & Costing</h3>
              <div className="grid-4">
                <div className="field">
                  <label>{tripType==='Delivery' ? 'Job' : 'RFQ'} <span className="required">*</span></label>
                  <select
                    value={assignedId}
                    onChange={e=>setAssignedId(e.target.value)}
                    className={errors.assignedId ? 'input-error' : ''}
                  >
                    <option value="">Select</option>
                    {tripType==='Delivery'
                      ? jobs.map(j => <option key={j.id} value={j.id}>{j.jobId} — {j.jobName || 'Untitled'}</option>)
                      : rfqs.map(r => <option key={r.id} value={r.id}>{r.rfqNo || r.rfqId || r.id}</option>)
                    }
                  </select>
                  {errors.assignedId && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>Trip Cost (Template)</label>
                  <select value={tripCostTemplateId} onChange={onPickTemplate}>
                    <option value="">— Choose a cost —</option>
                    {tripCosts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.label} — {Number(c.amount || 0).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Base Amount (ZAR)</label>
                  <input
                    type="number"
                    min="0"
                    step=".01"
                    value={baseCost}
                    onChange={e=>setBaseCost(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="field chk-field">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={sleepover}
                      onChange={e=>onToggleSleepover(e.target.checked)}
                    />
                    <span>Overnight (+R{OVERNIGHT_EXTRA})</span>
                  </label>
                </div>

                <div className="field">
                  <label>Total Trip Cost</label>
                  <div className="total-chip">R{totalCost.toFixed(2)}</div>
                </div>
              </div>

              {/* attachments */}
              <div className="upload-wrapper mt">
                <p className="hint">Files ({fileCount}/{MAX_FILES}) &nbsp;•&nbsp; Images / PDF / CSV</p>

                {/* existing */}
                {oldFiles.length>0 && (
                  <div className="files-grid">
                    {oldFiles.map((f,i)=>(
                      <div key={i} className={`file-chip ${removeOld.has(f.url)?'dim':''}`}>
                        <div className={`preview ${f.kind}`}>
                          {f.kind==='image'
                            ? <img src={f.url} alt={f.name}/>
                            : <span className="badge-kind">{f.kind.toUpperCase()}</span>}
                        </div>
                        <div className="fname" title={f.name}>{f.name}</div>
                        <button
                          type="button"
                          className="remove-chip"
                          onClick={()=>toggleRemoveOld(f.url)}
                          aria-label="Toggle remove"
                        >
                          {removeOld.has(f.url)? <FaTimes/> : <FaTrashAlt/>}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* drop zone */}
                <div
                  className={`upload-area ${dragActive?'drag':''}`}
                  onClick={openPicker}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  role="button"
                  tabIndex={0}
                >
                  <FaCloudUploadAlt size={34}/>
                  <p>{dragActive?'Drop to upload':'Click or drag new files here'}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,text/csv,.csv"
                    multiple hidden
                    onChange={handleInputFiles}
                  />
                </div>

                {/* new previews */}
                {fileMeta.length>0 && (
                  <div className="files-grid">
                    {fileMeta.map((f,i)=>(
                      <div key={i} className="file-chip">
                        <div className={`preview ${f.kind}`}>
                          {f.kind==='image'
                            ? <img src={f.preview} alt={f.name}/>
                            : <span className="badge-kind">{f.kind.toUpperCase()}</span>}
                        </div>
                        <div className="fname" title={f.name}>{f.name}</div>
                        <button type="button" className="remove-chip" onClick={()=>removeNew(i)}>
                          <FaTimes/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* actions */}
            <section className="card action-card">
              <div className="actions-row">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving?'Updating…':'Update Trip'}
                </button>
                <button type="button" className="btn-outline" onClick={()=>navigate(-1)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </section>
          </form>
        )}
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58}/>
          <p>Updating…</p>
        </div>
      )}
    </div>
  );
}
